// Revenue.tsx (with smooth wavy sparklines)
import React, { useState } from 'react'
import Products from './sections/Products'
import Channels from './sections/Channels'
import RevenueCharts from './sections/RevenueCharts'
import { CustomTab } from '@/components/UI/Tabs'
import { useGetTransactionsMonthlyStatsQuery } from '@/services/api/transaction/transactionApi'
import Loader from '@/components/UI/Loader/Loader'
import ModuleLoader from '@/components/UI/Loader/ModuleLoader'

const Revenue = () => {
  const [activeTab, setActiveTab] = useState(0)
  const { data: apiResponse, isLoading, isError, refetch } = useGetTransactionsMonthlyStatsQuery()

  // Extract data from API response
  const totalRevenue = apiResponse?.total_amount_so_far || 0
  const totalOrders = apiResponse?.total_orders_so_far || 0
  const avgOrderValue = apiResponse?.average_order_value || 0
  const monthlyData = apiResponse?.monthly_data || []
  const percentageChange = apiResponse?.percentage_change || {}

  // Enhanced smooth sparkline path generator using Catmull-Rom spline
  const generateSparklinePath = (data, width, height) => {
    if (!data || data.length === 0) return ''

    const max = Math.max(...data)
    const min = Math.min(...data)
    const range = max - min || max || 1

    const points = data.map((value, index) => {
      const x = (index / Math.max(data.length - 1, 1)) * width
      const normalizedValue = range > 0 ? (value - min) / range : 0.5
      const y = height - (normalizedValue * (height * 0.8)) - (height * 0.1)
      return { x, y }
    })

    if (points.length < 2) {
      return `M 0,${height / 2} L ${width},${height / 2} L ${width},${height} L 0,${height} Z`
    }

    // Catmull-Rom to Bezier curve conversion
    const catmullRomToBezier = (p0, p1, p2, p3) => {
      const cp1x = p1.x + (p2.x - p0.x) / 6
      const cp1y = p1.y + (p2.y - p0.y) / 6
      const cp2x = p2.x - (p3.x - p1.x) / 6
      const cp2y = p2.y - (p3.y - p1.y) / 6
      return { cp1x, cp1y, cp2x, cp2y }
    }

    let pathData = `M ${points[0].x.toFixed(2)},${points[0].y.toFixed(2)}`

    for (let i = 0; i < points.length - 1; i++) {
      const p0 = points[Math.max(i - 1, 0)]
      const p1 = points[i]
      const p2 = points[i + 1]
      const p3 = points[Math.min(i + 2, points.length - 1)]

      const { cp1x, cp1y, cp2x, cp2y } = catmullRomToBezier(p0, p1, p2, p3)
      pathData += ` C ${cp1x.toFixed(2)},${cp1y.toFixed(2)} ${cp2x.toFixed(2)},${cp2y.toFixed(2)} ${p2.x.toFixed(2)},${p2.y.toFixed(2)}`
    }

    // Close the path
    pathData += ` L ${width},${height} L 0,${height} Z`

    return pathData
  }

  // Enhanced data generator with better defaults
  const generateSparklineData = (dataKey, defaultValue = 0) => {
    if (!monthlyData || monthlyData.length === 0) {
      return Array(6).fill(defaultValue)
    }

    const recentData = monthlyData.slice(-12)

    return recentData.map(item => {
      const value = item?.[dataKey]
      return typeof value === 'number' ? value : defaultValue
    })
  }

  // Calculate average order value sparkline with proper handling
  const generateAvgOrderValueSparkline = () => {
    if (!monthlyData || monthlyData.length === 0) {
      return Array(6).fill(0)
    }

    const recentData = monthlyData.slice(-12)

    return recentData.map(item => {
      const amount = item?.total_amount || 0
      const orders = item?.total_orders || 0

      return orders > 0 ? amount / orders : 0
    })
  }

  // Format percentage change with proper sign
  const formatPercentageChange = (value) => {
    if (typeof value !== 'number') return '0.0%'
    const sign = value >= 0 ? '+' : ''
    return `${sign}${value.toFixed(1)}%`
  }

  // Metric card component
  const MetricCard = ({
    title,
    value,
    growthRate,
    color,
    gradientId,
    sparklineData,
    prefix = '₦',
    showDecimals = true
  }) => {
    const isNegative = parseFloat(growthRate) < 0
    const hasValidData = sparklineData && sparklineData.some(v => v > 0)

    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-shadow">
        <div className="flex flex-col">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-gray-600">{title}</h3>
            <div className={`flex items-center text-xs font-medium ${isNegative ? 'text-red-500' : 'text-green-500'}`}>
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d={isNegative ? "M13 17h8m0 0v-8m0 8l-8-8-4 4-6-6" : "M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"}
                />
              </svg>
              {growthRate}
            </div>
          </div>
          <p className="text-3xl font-bold text-gray-900 mb-4">
            {prefix}{typeof value === 'number'
              ? value.toLocaleString('en-NG', {
                minimumFractionDigits: showDecimals ? 2 : 0,
                maximumFractionDigits: showDecimals ? 2 : 0
              })
              : value}
          </p>
          <div className="h-12 -mb-2">
            {hasValidData ? (
              <svg viewBox="0 0 100 30" className="w-full h-full" preserveAspectRatio="none">
                <defs>
                  <linearGradient id={gradientId} x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor={color} stopOpacity="0.4" />
                    <stop offset="50%" stopColor={color} stopOpacity="0.2" />
                    <stop offset="100%" stopColor={color} stopOpacity="0.05" />
                  </linearGradient>
                </defs>
                <path
                  d={generateSparklinePath(sparklineData, 100, 30)}
                  fill={`url(#${gradientId})`}
                  stroke={color}
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  vectorEffect="non-scaling-stroke"
                />
              </svg>
            ) : (
              <div className="flex items-center justify-center h-full text-xs text-gray-400">
                No data available
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  const tabs = [
    { label: "Overview", content: <RevenueCharts /> },
    { label: "Products", content: <Products /> },
    // { label: "Channels", content: <Channels /> },
  ]

  if (isLoading) {
    return (
      <div className="p-6 flex  h-full">
        <ModuleLoader text="Loading revenue data..." />
      </div>
    )
  }

  if (isError) {
    return (
      <div className="p-6 flex items-center justify-center min-h-screen">
      <div className='flex flex-col items-center'>
         <div className="text-red-500">Error loading revenue data</div>
        <button
          onClick={() => refetch()}
          className="mt-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          Retry
        </button>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6 bg-gray-50">
      {/* Revenue Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <MetricCard
          title="Total Revenue"
          value={totalRevenue}
          growthRate={formatPercentageChange(percentageChange.amount_change_percentage)}
          color="#fb923c"
          gradientId="gradient-total"
          sparklineData={generateSparklineData('total_amount')}
        />
        <MetricCard
          title="Total Orders"
          value={totalOrders}
          growthRate={formatPercentageChange(percentageChange.order_change_percentage)}
          color="#10b981"
          gradientId="gradient-orders"
          sparklineData={generateSparklineData('total_orders')}
          prefix=""
          showDecimals={false}
        />
        <MetricCard
          title="Avg Order Value"
          value={avgOrderValue}
          growthRate={formatPercentageChange(percentageChange.average_value_change_percentage)}
          color="#8b5cf6"
          gradientId="gradient-avg"
          sparklineData={generateAvgOrderValueSparkline()}
        />
      </div>

      {/* Tabbed Chart Section */}
      <div className="bg-white rounded-xl p-6">
        <CustomTab tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} align='right' />
      </div>
    </div>
  )
}

export default Revenue